import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import fetchWithAuth from "../fetchWithAuth";

export function useBulkRowSelection(items, setItems, { projectId, resource, onChange }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const currentItems = useRef(items);
  currentItems.current = items;
  useEffect(() => {
    const ids = new Set(items.map(item => item.id));
    setSelectedIds(current => {
      const next = current.filter(id => ids.has(id));
      return next.length === current.length ? current : next;
    });
  }, [items]);
  useEffect(() => { setSelectedIds([]); setOpen(false); setError(""); setNotice(""); }, [projectId]);
  const selected = new Set(selectedIds);
  const toggle = id => setSelectedIds(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
  const all = items.length > 0 && items.every(item => selected.has(item.id));
  const toggleAll = () => setSelectedIds(all ? [] : items.map(item => item.id));
  const remove = async () => {
    if (!selectedIds.length || busy) return;
    setBusy(true); setError(""); setNotice("");
    const ids = [...selectedIds];
    try {
      const response = await fetchWithAuth(`/api/project/${projectId}/${resource}/bulk-delete`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ids }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Не удалось удалить выбранные записи");
      const deleted = new Set(ids);
      const remaining = currentItems.current.filter(item => !deleted.has(item.id));
      setItems(remaining);
      onChange?.(remaining);
      setSelectedIds([]); setOpen(false);
      setNotice(`Удалено записей: ${data.deletedCount}`);
    } catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  return { selectedIds, selected, all, partial: !all && selectedIds.length > 0, toggle, toggleAll,
    clear: () => setSelectedIds([]), open, setOpen, busy, error, notice, remove };
}

export function SelectionCheckbox({ checked, partial = false, onChange, label, disabled = false }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = partial; }, [partial]);
  return <input ref={ref} type="checkbox" className="h-4 w-4 cursor-pointer accent-blue-600 disabled:cursor-default"
    aria-label={label} checked={checked} onChange={onChange} disabled={disabled} />;
}

export function BulkDeleteBar({ selection, label }) {
  const count = selection.selectedIds.length;
  return <>
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-gray-50 p-3" aria-label={`Групповые действия: ${label}`}>
      <span className="text-sm text-gray-600">Выбрано: <strong>{count}</strong></span>
      <Button type="button" variant="outline" title="Удалить выбранные" aria-label="Удалить выбранные"
        className="h-8 w-8 border-rose-200 bg-white p-0 text-rose-600 shadow-none hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
        disabled={!count || selection.busy} onClick={() => selection.setOpen(true)}>
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
      {count > 0 && <Button variant="ghost" disabled={selection.busy} onClick={selection.clear}>Снять выделение</Button>}
      {selection.notice && <span role="status" className="text-sm text-green-700">{selection.notice}</span>}
      {selection.error && !selection.open && <span role="alert" className="text-sm text-red-700">{selection.error}</span>}
    </div>
    <Dialog open={selection.open} onOpenChange={value => { if (!selection.busy) selection.setOpen(value); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Удалить выбранные записи?</DialogTitle>
          <DialogDescription>{label}: {count}. Будут удалены только отмеченные записи текущего проекта.</DialogDescription>
        </DialogHeader>
        {selection.error && <p role="alert" className="text-sm text-red-700">{selection.error}</p>}
        <DialogFooter>
          <Button variant="outline" disabled={selection.busy} onClick={() => selection.setOpen(false)}>Отмена</Button>
          <Button variant="destructive" disabled={selection.busy || !count} onClick={selection.remove}>{selection.busy ? "Удаление…" : `Удалить (${count})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
